import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Check, ArrowRight, Building2, Smartphone, Globe, Clock, Users, FileText, Calculator, BarChart3, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';

function AppGestionCopropriete() {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Application Gestion Copropriété en Ligne 2026 | App Copro Simple</title>
        <meta name="description" content="Application de gestion de copropriété en ligne. App copro complète pour syndics bénévoles : AG, appels de fonds, comptabilité. Accessible sur mobile. C'est gratuit." />
        <meta name="keywords" content="application gestion copropriété, app copropriété, app copro, gestion copropriété en ligne, application syndic, logiciel copropriété mobile" />
        <link rel="canonical" href="https://copropriete.app/application-gestion-copropriete" />
      </Helmet>

      {/* Header */}
      <header className="bg-slate-900 text-white py-4">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold flex items-center gap-2">
            <Building2 className="w-6 h-6" />
            copropriete.app
          </Link>
          <Link to="/login" className="bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-lg text-sm font-medium">
            C'est gratuit
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-white/20 px-4 py-1 rounded-full text-sm font-medium mb-6">
                #1 des apps de copropriété
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                L'application de gestion de copropriété <span className="text-indigo-200">simple et efficace</span>
              </h1>
              <p className="text-xl text-indigo-100 mb-8">
                Gérez votre copropriété en ligne depuis votre smartphone ou ordinateur. L'app copro tout-en-un pour les syndics bénévoles.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/login">
                  <Button size="lg" className="bg-white text-indigo-600 hover:bg-indigo-50 text-lg px-8">
                    Essayer gratuitement <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
              <div className="flex items-center gap-4 mt-6 text-sm text-indigo-200">
                <span className="flex items-center gap-1"><Check className="w-4 h-4" /> 14 jours gratuits</span>
                <span className="flex items-center gap-1"><Check className="w-4 h-4" /> Sans carte bancaire</span>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="flex items-center gap-2 mb-4">
                  <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                </div>
                <p className="text-indigo-100 italic mb-4">"Enfin une app de copro simple à utiliser ! J'ai pu organiser mon AG en 10 minutes."</p>
                <p className="text-sm text-indigo-200">— Marie D., Syndic bénévole à Lyon</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Une app de copropriété complète
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Toutes les fonctionnalités dont vous avez besoin pour gérer votre copropriété en ligne, accessibles depuis n'importe quel appareil.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Users, title: "Gestion des copropriétaires", desc: "Annuaire complet avec tantièmes, lots, contacts. Import automatique depuis l'EDD." },
              { icon: FileText, title: "Assemblées générales", desc: "Convocations légales, feuilles de présence, procès-verbaux. Tout en quelques clics." },
              { icon: Calculator, title: "Appels de fonds", desc: "Calcul automatique selon les tantièmes. Génération PDF et suivi des paiements." },
              { icon: BarChart3, title: "Comptabilité simplifiée", desc: "Budget prévisionnel, suivi des dépenses, régularisation de charges." },
              { icon: Globe, title: "100% en ligne", desc: "Aucune installation. Accédez à votre copropriété depuis n'importe où." },
              { icon: Smartphone, title: "Mobile friendly", desc: "Application optimisée pour smartphone. Gérez votre copro en déplacement." },
            ].map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-xl border border-slate-200 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-indigo-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why App */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
            Pourquoi utiliser une application de gestion de copropriété ?
          </h2>

          <div className="space-y-8">
            <div className="bg-white rounded-xl p-8 shadow-sm">
              <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                <Clock className="w-6 h-6 text-indigo-600" />
                Gagnez du temps
              </h3>
              <p className="text-slate-600">
                Fini les heures passées sur Excel ou Word. Notre <strong>application de copropriété</strong> automatise les tâches répétitives : calcul des charges selon les tantièmes, génération des convocations, procès-verbaux pré-remplis... Vous gagnez plusieurs heures chaque mois.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm">
              <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                <Globe className="w-6 h-6 text-indigo-600" />
                Accessible partout
              </h3>
              <p className="text-slate-600">
                Notre <strong>app de copro en ligne</strong> est accessible depuis n'importe quel appareil connecté à Internet. Que vous soyez chez vous, au travail ou en déplacement, vos documents et données sont toujours à portée de main.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm">
              <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                <Users className="w-6 h-6 text-indigo-600" />
                Collaborez avec les copropriétaires
              </h3>
              <p className="text-slate-600">
                Chaque copropriétaire peut accéder à son espace personnel pour consulter ses appels de fonds, les documents de la copropriété et participer aux échanges. La <strong>gestion de copropriété en ligne</strong> facilite la communication.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Tarif simple et transparent
          </h2>
          <p className="text-slate-600 mb-12">
            Une seule offre avec toutes les fonctionnalités incluses
          </p>

          <div className="bg-white rounded-2xl border-2 border-indigo-600 p-8 max-w-md mx-auto">
            <div className="text-indigo-600 font-semibold mb-2">PREMIUM</div>
            <div className="text-4xl font-bold text-slate-900 mb-2">
              49€<span className="text-lg font-normal text-slate-500">/mois</span>
            </div>
            <div className="text-slate-500 mb-6">ou 490€/an (2 mois offerts)</div>
            
            <ul className="text-left space-y-3 mb-8">
              {[
                "Copropriétaires illimités",
                "Assemblées générales illimitées",
                "Appels de fonds automatiques",
                "Documents et stockage illimité",
                "Assistant juridique IA",
                "Support prioritaire"
              ].map((item, index) => (
                <li key={index} className="flex items-center gap-2 text-slate-700">
                  <Check className="w-5 h-5 text-emerald-500" />
                  {item}
                </li>
              ))}
            </ul>

            <Link to="/login">
              <Button className="w-full bg-indigo-600 hover:bg-indigo-700">
                C'est gratuit 14 jours
              </Button>
            </Link>
            <p className="text-sm text-slate-500 mt-3">Sans engagement • Sans carte bancaire</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Prêt à simplifier la gestion de votre copropriété ?
          </h2>
          <p className="text-slate-400 mb-8 text-lg">
            Rejoignez les syndics bénévoles qui utilisent notre application de copropriété
          </p>
          <Link to="/login">
            <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-lg px-8">
              Démarrer maintenant <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-8">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-sm">© 2026 copropriete.app - Application de gestion de copropriété en ligne</p>
        </div>
      </footer>
    </div>
  );
}

export default AppGestionCopropriete;
